/**
 * GiocatoreSquadraSelector — dropdown dei giocatori in rosa di una squadra
 * per una specifica stagione.
 *
 * Usato nel wizard mercato per selezionare giocatori "in entrata" (dalla
 * squadra destinataria) o "in uscita" (dalla propria squadra).
 */

import { useRosa } from "../../hooks/useFase2";
import { sigleRuoli } from "./nomenclatura";
import { tr } from "../../i18n";

const RUOLO_LABEL: Record<string, string> = {
  P: "Por",
  D: "Dif",
  C: "Cen",
  A: "Att",
};

interface GiocatoreSquadraSelectorProps {
  stagioneId: string | null;
  squadraId: string | null;
  value: string;
  onChange: (giocatoreId: string) => void;
  /** Quali giocatori escludere (es. quelli gia' selezionati altrove) */
  excludeIds?: string[];
  label?: string;
  className?: string;
  /** Se true, mostra solo i giocatori cedibili (tipo_possesso='proprieta').
   * Andrea 2026-05-19: per le operazioni mercato in uscita (vendita/scambio)
   * non si possono mettere giocatori presi in prestito. */
  soloCedibili?: boolean;
}

function classicLetter(r: string | null): string {
  if (!r) return "?";
  const c = r.charAt(0).toUpperCase();
  return ["P", "D", "C", "A"].includes(c) ? c : "?";
}

export function GiocatoreSquadraSelector({
  stagioneId,
  squadraId,
  value,
  onChange,
  excludeIds = [],
  label,
  className = "",
  soloCedibili = false,
}: GiocatoreSquadraSelectorProps) {
  const { data: rosa, isLoading } = useRosa(stagioneId, squadraId);

  const items = (rosa?.items ?? []).filter((p) => {
    if (excludeIds.includes(p.giocatore_id)) return false;
    // Filtro cedibilità: i prestati non possono essere venduti/scambiati.
    if (soloCedibili) {
      // tipo_possesso può essere undefined sui dati vecchi → in tal caso
      // accetta (compatibilità rosa caricata prima del fix backend).
      if (p.tipo_possesso != null && p.tipo_possesso !== "proprieta") {
        return false;
      }
    }
    return true;
  });

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs text-fg-muted uppercase tracking-wider mb-1">{label}</label>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!stagioneId || !squadraId || isLoading || !items.length}
        className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
      >
        {!squadraId && <option value="">{tr("— seleziona squadra prima —")}</option>}
        {squadraId && isLoading && <option>{tr("Caricamento…")}</option>}
        {squadraId && !isLoading && !items.length && (
          <option value="">{tr("— nessun giocatore in rosa —")}</option>
        )}
        {squadraId && !isLoading && items.length > 0 && (
          <>
            <option value="">{tr("— seleziona giocatore —")}</option>
            {items.map((p) => {
              const r = classicLetter(p.ruolo_classic);
              const ruoli =
                (p.ruoli_mantra ? sigleRuoli(p.ruoli_mantra) : null) ?? RUOLO_LABEL[r] ?? r;
              return (
                <option key={p.giocatore_id} value={p.giocatore_id}>
                  {p.nome} ({ruoli}){p.club_serie_a ? ` · ${p.club_serie_a}` : ""}
                </option>
              );
            })}
          </>
        )}
      </select>
    </div>
  );
}
